import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { KeyedMutator } from "swr";
import {
  aggregate,
  buildForceScrapePostTagFilters,
  encodePostTagsForReportUrl,
  formatNumber,
  normalizeRealCampaignPostTags,
  POST_TAG_FILTER_ALL,
  postMatchesCampaignPostTagSelection,
} from "@/lib/util";
import Loader from "@/components/CustomUiComponents/Loader";
import { Badge, Flex, Heading, IconButton, Tabs, Text } from "@radix-ui/themes";
import NewMultiSelect from "../../CustomUiComponents/NewMultiSelect";
import MassPostImport from "../../Modals/NewMassImport";
import NewCreateReport from "../../Modals/NewCreateReport";
import NewShareLink from "../../Modals/NewShareLink";
import Playlists from "../Playlists";
import PostsTab from "./PostsTab";
import SoundsTab from "./SoundsTab";
import CreatorsTab from "./CreatorsTab";
import NewAddBudget from "@/components/Modals/NewAddBudget";
import CampaignMassAddCreator from "@/components/Modals/CampaignMassAddCreator";
import SettingsTab from "./SettingsTab";
import TrackedCreatorsSection from "./TrackedCreatorsSection";
import useMobile from "@/lib/useMobile";
import { useCreatorTags } from "@/services/creator";
import { useCampaignBudgets } from "@/services/campaign";
import { useEngagements } from "@/services/engagement";
import { IconX } from "@tabler/icons-react";
import { useRouter } from "next/router";
import DateFilter from "@/components/CustomUiComponents/DateFilter";
import useUser from "@/lib/useUser";
import fetchJson from "@/lib/fetchJson";
import DeleteCampaign from "@/components/Modals/DeleteCampaign";
import MergeCampaign from "@/components/Modals/MergeCampaign";
import { isMetaReviewEmail } from "@/lib/constants";
import TrackingProgress from "./TrackingProgress";
import OutreachAuditTab from "./OutreachAuditTab";
import OutreachKanban from "./OutreachKanban";
import DraftsTab from "./DraftsTab";
import ScrapeCreators from "@/components/Modals/ScrapeCreators";

const Totals = ({ title, value }: { title: string; value: string }) => (
  <Flex direction="column" gap="2">
    <Text size="3">{title}</Text>
    <Heading size="8">{value}</Heading>
  </Flex>
);

interface Props {
  editable: boolean;
  shareable: boolean;
  reportable: boolean;
  transferable: boolean;
  data: Campaign | undefined;
  mutate: KeyedMutator<Campaign>;
}

type ValuePiece = Date | null;

type Value = ValuePiece | [ValuePiece, ValuePiece];

const AdvancedView = ({
  data,
  editable,
  mutate,
  shareable,
  transferable,
  reportable,
}: Props) => {
  const ALL_TYPES = ["Tiktok", "Instagram", "Twitter", "Youtube"];
  const DEFAULT_TYPES = ["Tiktok", "Instagram"];
  const { data: creatorTags } = useCreatorTags();
  const {
    user,
    canCreateCampaigns,
    canEditCampaigns,
    canManageUsers,
    canImpersonate,
    canForceScrape,
  } = useUser();
  const { data: campaignBudgets } = useCampaignBudgets(data?.id as any);
  const { data: engagements } = useEngagements(data?.id);
  // "Active outreach" = engagements not in terminal states. Mirrors what's
  // shown as draggable columns in the kanban (see OutreachKanban).
  const activeOutreachCount = useMemo(() => {
    if (!Array.isArray(engagements)) return 0;
    const TERMINAL_STATES = new Set(["Posted", "Declined", "Canceled"]);
    return engagements.filter(
      (e: { state?: string }) => !TERMINAL_STATES.has(e.state ?? "")
    ).length;
  }, [engagements]);
  const [types, setTypes] = useState<string[]>(ALL_TYPES);
  const [sortKey, setSortKey] = useState("views");
  const [sortDir, setSortDir] = useState("desc");
  const [tags, setTags] = useState([] as string[]);
  const [selectedTags, setSelectedTags] = useState([] as string[]);
  const [selectedPostTags, setSelectedPostTags] = useState<string[]>([
    POST_TAG_FILTER_ALL,
  ]);
  const [filterDates, setFilterDates] = useState<Value>([null, null]);
  const [posts, setPosts] = useState([]);
  const [scraping, setScraping] = useState(false);
  const { isMobile } = useMobile();
  const router = useRouter();
  const isAdmin = canManageUsers || canImpersonate;
  // Meta App Review account: only the outreach tab is shown.
  const isReviewer = isMetaReviewEmail(user?.email);

  // TODO: fix this hard coded emails
  //
  //
  const creatorsPassthroughEmails = [
    "john@creatorxchange.co",
    "Johnmahoney@creatorxchange.co",
    "kal@creatorxchange.co",
  ];
  const isCreatorsPassThrough = creatorsPassthroughEmails.includes(
    user?.email || ""
  );

  const playlists = data?.post?.filter(
    ({ type }) => type === "spotify-playlist"
  );

  const currentTags = Array.from(new Set(data?.tags)).sort();
  if (JSON.stringify(tags) != JSON.stringify(currentTags)) {
    setTags(currentTags);
  }

  const allPostTagOptions = useMemo(
    () => normalizeRealCampaignPostTags(data?.tags as string[] | undefined),
    [data?.tags],
  );

  const postTagMultiSelectOptions = useMemo(
    () => [POST_TAG_FILTER_ALL, ...allPostTagOptions],
    [allPostTagOptions],
  );

  const allPostTagOptionsKey = useMemo(
    () => allPostTagOptions.join("\u0001"),
    [allPostTagOptions],
  );

  useEffect(() => {
    if (data?.id == null) return;
    if (allPostTagOptions.length === 0) {
      setSelectedPostTags([]);
    } else {
      setSelectedPostTags([POST_TAG_FILTER_ALL]);
    }
  }, [data?.id, allPostTagOptionsKey, allPostTagOptions.length]);

  const setPostTagSelection = useCallback((next: string[]) => {
    setSelectedPostTags(() => {
      const hasAll = next.includes(POST_TAG_FILTER_ALL);
      const reals = next.filter((t) => t !== POST_TAG_FILTER_ALL);
      if (hasAll) {
        return [POST_TAG_FILTER_ALL];
      }
      return reals;
    });
  }, []);

  const postsForReportBase = useMemo(
    () =>
      data?.post
        ?.filter(({ type }) =>
          ["instagram", "tiktok", "twitter", "youtube"].includes(type)
        )
        .filter((post) =>
          postMatchesCampaignPostTagSelection(
            post,
            selectedPostTags,
            allPostTagOptions,
          ),
        )
        .filter((post) => types.map((i) => i.toLowerCase()).includes(post.type)) ??
      [],
    [data?.post, types, selectedPostTags, allPostTagOptions],
  );

  useEffect(() => {
    setPosts(
      postsForReportBase.filter((post) => {
        const postedAt = new Date(post.postedAt);
        let valid = true;
        if (filterDates === null) {
          return true;
        }
        if (filterDates instanceof Date) {
          return postedAt > filterDates;
        }
        if (filterDates[0] !== null && postedAt <= filterDates[0]) {
          valid = false;
        }
        if (filterDates[1] !== null && postedAt >= filterDates[1]) {
          valid = false;
        }
        return valid;
      })
    );
  }, [postsForReportBase, filterDates]);

  const audio = data?.post?.filter(({ type }) =>
    ["instagram-audio", "tiktok-audio"].includes(type)
  );
  const hashtags = data?.hashtags;

  const _forceScrapePosts = async () => {
    setScraping(true);
    const tagFilters = buildForceScrapePostTagFilters(
      selectedPostTags,
      allPostTagOptions,
    );
    await fetchJson(`/campaign/${data.id}/scrape`, {
      method: "POST",
      body: JSON.stringify({
        platform: types.map((type) => type.toLowerCase()),
        ...tagFilters,
        postFormat: selectedTags,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    setScraping(false);
    alert(
      "Posts are queued to be re-scraped, please wait 10 minutes before checking again"
    );
  };
  if (!data) {
    return <Loader full={false} />;
  }

  return (
    <>
      <Flex direction="row" justify="between" gap="4" pb="4">
        <Flex direction="row" gap="2" align="center">
          <Heading size="6">Campaign Report</Heading>
        </Flex>
        <Flex gap="5">
          {canForceScrape && (
            <ScrapeCreators
              campaignId={data.id}
              mutate={mutate}
              disabled={scraping}
            />
          )}
          {!isReviewer && reportable && (
            <NewCreateReport
              campaign={data}
              tags={selectedPostTags}
              tagsForReportUrl={encodePostTagsForReportUrl(
                selectedPostTags,
                allPostTagOptions,
              )}
              sortDir={sortDir}
              sortKey={sortKey}
              postsForReportBase={postsForReportBase}
              defaultReportDates={filterDates}
              types={types}
            />
          )}
          {!isReviewer && shareable && (
            <NewShareLink
              id={`${window.location.protocol}//${window.location.hostname}/share/${data.shareCode}`}
            />
          )}
          {isAdmin && <MergeCampaign campaignId={data.id} />}
          {isAdmin && <DeleteCampaign campaignId={data.id} />}
        </Flex>
      </Flex>
      <Tabs.Root defaultValue={isReviewer ? "tracker" : "posts"}>
        <Tabs.List size="2">
          {!isReviewer && (
            <Tabs.Trigger value="posts">
              <Flex direction="row" gap="1">
                Posts
                <Badge>{posts && posts.length}</Badge>
              </Flex>
            </Tabs.Trigger>
          )}
          {!isReviewer && (
            <Tabs.Trigger value="tracking">
              <Flex direction="row" gap="1">
                Tracking
                <Badge>
                  {(audio?.length || 0) +
                    (hashtags?.length || 0) +
                    (data.trackedCreators?.length || 0)}
                </Badge>
              </Flex>
            </Tabs.Trigger>
          )}
          {(isAdmin || isReviewer) && data && (
            <Tabs.Trigger value="tracker">
              <Flex direction="row" gap="1">
                {isReviewer ? "Outreach" : "Active Campaign Tracker"}
                <Badge>{activeOutreachCount}</Badge>
              </Flex>
            </Tabs.Trigger>
          )}
          {!isReviewer && (editable || isCreatorsPassThrough) && (
            <Tabs.Trigger value="creators">
              <Flex direction="row" gap="1">
                Creators
                <Badge>{data.budgets && data.budgets.length}</Badge>
              </Flex>
            </Tabs.Trigger>
          )}
          {!isReviewer && (
            <Tabs.Trigger value="playlists">
              <Flex direction="row" gap="1">
                Playlists
                <Badge>{playlists && playlists.length}</Badge>
              </Flex>
            </Tabs.Trigger>
          )}
          {!isReviewer &&
            (canCreateCampaigns || canEditCampaigns || canManageUsers) && (
              <Tabs.Trigger value="settings">Settings</Tabs.Trigger>
            )}
          {(canManageUsers || canImpersonate) && (
            <Tabs.Trigger value="tracking-progress">
              Tracking Progress
            </Tabs.Trigger>
          )}
          {isAdmin && data && (
            <Tabs.Trigger value="outreach-audit">Outreach Progress</Tabs.Trigger>
          )}
        </Tabs.List>

        {!isReviewer && (
        <Tabs.Content value="posts">
          <Flex direction="column" gap="5" pt="4">
            <Flex className="box" gap="5" p="5" direction="column">
              <Flex direction="row" gap="2" justify="between" align="center">
                <Heading size="4">Post Analytics</Heading>
                <Flex id="dashboard-analytics-filters" direction="row" gap="3">
                  <DateFilter
                    setFilterDates={setFilterDates}
                    filterDates={filterDates}
                  />

                  {allPostTagOptions.length > 0 ? (
                    <NewMultiSelect
                      transparent
                      style={{ width: 150, marginRight: 10 }}
                      setSelected={setPostTagSelection}
                      title="Tags"
                      selected={selectedPostTags}
                      options={postTagMultiSelectOptions as any}
                      getOptionLabel={(v) =>
                        v === POST_TAG_FILTER_ALL ? "All tags" : v
                      }
                    />
                  ) : null}
                  <NewMultiSelect
                    transparent
                    style={{ width: 140, marginRight: 10 }}
                    setSelected={setTypes}
                    title="Platform"
                    selected={types}
                    options={ALL_TYPES}
                  />
                </Flex>
              </Flex>
              <Flex direction="row" gap="2" justify="between">
                <Totals
                  title="Views"
                  value={formatNumber(aggregate(posts, "views"))}
                />
                <Totals
                  title="Likes"
                  value={formatNumber(aggregate(posts, "likes"))}
                />
                <Totals
                  title="Shares"
                  value={formatNumber(aggregate(posts, "shares"))}
                />
                <Totals
                  title="Comments"
                  value={formatNumber(aggregate(posts, "comments"))}
                />
              </Flex>
            </Flex>
            <PostsTab
              fullPosts={posts || []}
              mutate={mutate}
              editable={editable}
              id={data?.id}
            />
          </Flex>
        </Tabs.Content>
        )}

        {!isReviewer && (
        <Tabs.Content value="tracking">
          <Flex direction="column" pt="4" gap="6">
            {/* Tracked Creators Section */}
            <Flex direction="column" gap="3">
              <Flex
                direction="row"
                justify="between"
                pl="1"
                px={isMobile ? "5" : ""}
              >
                <Heading size="4">Creator Tracking</Heading>
              </Flex>
              <Flex className="box" direction="column" p="5">
                <TrackedCreatorsSection
                  campaignId={data.id}
                  onUpdate={mutate}
                />
              </Flex>
            </Flex>

            {/* Sounds & Hashtags Section */}
            <Flex direction="column" gap="3">
              <Flex
                direction="row"
                justify="between"
                pl="1"
                px={isMobile ? "5" : ""}
              >
                <Heading size="4">Sounds & Hashtags</Heading>
                {editable && (
                  <MassPostImport
                    type="sounds"
                    mutate={mutate}
                    campaignId={data.id}
                  />
                )}
              </Flex>
              <Flex className="box" direction="column" p="5">
                <SoundsTab
                  audio={audio || []}
                  hashtags={hashtags || []}
                  mutate={mutate}
                  editable={editable}
                />
              </Flex>
            </Flex>
          </Flex>
        </Tabs.Content>
        )}

        {(isAdmin || isReviewer) && data && (
          <Tabs.Content value="tracker">
            <Flex direction="column" pt="4" gap="3">
              <Tabs.Root defaultValue="outreach">
                {!isReviewer && (
                  <Tabs.List size="2" style={{ paddingLeft: 4 }}>
                    <Tabs.Trigger value="outreach">Outreach</Tabs.Trigger>
                    <Tabs.Trigger value="drafts">
                      <Flex direction="row" gap="1" align="center">
                        Drafts
                        <Badge size="1">
                          {(data.postDrafts && data.postDrafts.length) || 0}
                        </Badge>
                      </Flex>
                    </Tabs.Trigger>
                  </Tabs.List>
                )}
                <Tabs.Content value="outreach">
                  <Flex className="box" direction="column" p="5" mt="3">
                    <OutreachKanban campaign={data} mutate={mutate} />
                  </Flex>
                </Tabs.Content>
                {!isReviewer && (
                <Tabs.Content value="drafts">
                  <Flex direction="column" gap="3" mt="3">
                    <Flex direction="row" justify="between" pl="1">
                      <Heading size="4">Post Drafts</Heading>
                    </Flex>
                    <Flex className="box" direction="column" p="5">
                      <DraftsTab
                        drafts={data.postDrafts}
                        mutate={mutate}
                        editable={editable}
                      />
                    </Flex>
                  </Flex>
                </Tabs.Content>
                )}
              </Tabs.Root>
            </Flex>
          </Tabs.Content>
        )}

        {!isReviewer && (editable || isCreatorsPassThrough) && (
          <Tabs.Content value="creators">
            <Flex direction="column" pt="4" gap="3">
              <Flex direction="row" justify="between" pl="1">
                <Flex direction="row" justify="between" pl="1" align={"center"}>
                  <Heading size="4">Creators</Heading>
                  <NewMultiSelect
                    transparent
                    style={{ width: 140, marginRight: 10 }}
                    setSelected={setTypes}
                    title="Platform"
                    selected={types}
                    options={ALL_TYPES}
                  />
                  <NewMultiSelect
                    transparent
                    style={{ width: 150, marginRight: 10 }}
                    setSelected={setSelectedTags}
                    title="Post Formats"
                    selected={selectedTags}
                    options={
                      Array.from(
                        (
                          new Set(creatorTags?.map((t) => t.id)) as any
                        ).intersection(
                          new Set(campaignBudgets?.map((b) => b.creatorTypeId))
                        )
                      )
                        .map((b) => creatorTags?.find((t) => t.id === b)?.tag)
                        .sort() as any
                    }
                  />
                  {selectedTags.map((tag) => {
                    return (
                      <Badge radius="large" key={tag} mr="1">
                        {tag}
                        <IconButton
                          variant="ghost"
                          onClick={() =>
                            setSelectedTags(
                              [...selectedTags].filter((t) => t !== tag)
                            )
                          }
                        >
                          <IconX className="icon-accent" height="10px" />
                        </IconButton>
                      </Badge>
                    );
                  })}
                </Flex>
                <Flex direction="row" gap="2">
                  <CampaignMassAddCreator
                    mutate={mutate}
                    campaignId={data.id}
                  />
                  <NewAddBudget mutate={mutate} campaignId={data.id} />
                </Flex>
              </Flex>
              <CreatorsTab
                platforms={types}
                campaign={data}
                mutate={mutate}
                selectedTags={selectedTags}
              />
            </Flex>
          </Tabs.Content>
        )}

        {!isReviewer && (
        <Tabs.Content value="playlists">
          <Flex direction="column" pt="4">
            <Flex className="box" p="5">
              <Playlists
                fullPlaylists={playlists}
                spotifyPlaylists={JSON.parse(
                  (data?.song && data.song.playlists) || "{}"
                )}
              />
            </Flex>
          </Flex>
        </Tabs.Content>
        )}

        <Tabs.Content value="linkpages"></Tabs.Content>

        {!isReviewer &&
          (canCreateCampaigns || canEditCampaigns || canManageUsers) && (
          <Tabs.Content value="settings">
            <SettingsTab
              transferable={transferable}
              campaign={data}
              mutate={mutate}
            />
          </Tabs.Content>
        )}
        {(canManageUsers || canImpersonate) && (
          <Tabs.Content value="tracking-progress">
            <TrackingProgress campaign={data} mutate={mutate} />
          </Tabs.Content>
        )}
        {isAdmin && data && (
          <Tabs.Content value="outreach-audit">
            <OutreachAuditTab campaignId={data.id} />
          </Tabs.Content>
        )}
      </Tabs.Root>
    </>
  );
};
export default memo(AdvancedView);
